import { useEffect } from "react";

/**
 * Hook for keeping track of clicks made outside or inside of an element.
 * If closeOnClick is set to 'outside' clicks that are outside of the anchor
 * element will trigger the dismiss handler. If closeOnClick is set to 'inside', a
 * click inside the anchor will trigger the dismiss handler.
 * It optionally also handles escape key, defaults to false.
 * @returns the element reference
 */
export function useDismiss<T extends HTMLElement = HTMLElement>({
  handler,
  enabled = true,
  reference,
  closeOnClick = "outside",
  escapeKey = false,
  tabKey = false,
}: {
  handler: () => void;
  enabled?: boolean;
  /** Anchor element ref */
  reference: React.RefObject<HTMLElement | null>;
  /** Close on click outside or inside of the anchor or disable. Defaults to outside. */
  closeOnClick?: "outside" | "inside" | "disabled";
  /** Close on escape key. Defaults to false. */
  escapeKey?: boolean;
  /** Close on tab key. Defaults to false. */
  tabKey?: boolean;
}): void {
  useEffect(() => {
    if (!enabled) return;

    function onKeyDown(event: KeyboardEvent): void {
      if (
        (escapeKey && event.key === "Escape") ||
        (tabKey && event.key === "Tab")
      ) {
        event.preventDefault();
        handler();
      }
    }

    const onClick = (event: MouseEvent): void => {
      if (!reference.current) return;
      const referenceWasClicked = reference.current.contains(event.target as T);

      if (
        (closeOnClick == "inside" && referenceWasClicked) ||
        (closeOnClick == "outside" && !referenceWasClicked)
      ) {
        event.stopPropagation(); // Prevent re-opening
        event.preventDefault();
        handler();
      }
    };

    closeOnClick !== "disabled" &&
      document.addEventListener("click", onClick, true);
    (escapeKey || tabKey) && document.addEventListener("keydown", onKeyDown);

    return () => {
      closeOnClick !== "disabled" &&
        document.removeEventListener("click", onClick, true);
      (escapeKey || tabKey) &&
        document.removeEventListener("keydown", onKeyDown);
    };
  }, [enabled, handler, escapeKey, tabKey, closeOnClick]);
}
